import { Selector } from '@ngxs/store';
import { cloneDeep } from 'lodash';

import {
    CheckPersonRulesMatrixModel,
    CourtForAddBookingModel,
    CourtForAddBookingOptionsModel,
    CourtForAddBookingTimeSlotsModel,
    PersonModel
} from '@bayclubs/court-booking-employee-api';
import { BcStateListContext } from '@bayclubs/state';

import { CourtBookingSaveTimeSlotModel } from '../models/court-booking-save-form.model';
import { CourtBookingSaveState } from './court-booking-save.state';
import { CourtBookingSaveStateModel } from './court-booking-save.state.model';

export class CourtBookingSaveSelectors {
    @Selector([CourtBookingSaveState, CourtBookingSaveSelectors.isMainPlayerSelected])
    static firstPlayerRulesMatrix(state: CourtBookingSaveStateModel, isMainPlayerSelected: boolean): CheckPersonRulesMatrixModel | null {
        if (state.playerRules.hasItems && isMainPlayerSelected) {
            return state.playerRules.items.find(pr => pr.personId === state.courtBookingForm.model.firstPlayer.personId);
        } else {
            return null;
        }
    }

    @Selector([CourtBookingSaveSelectors.firstPlayerRulesMatrix])
    static firstPlayerCanBook(personRules: CheckPersonRulesMatrixModel): boolean {
        if (personRules) {
            return !(
                personRules.courtBookingNotAllowed ||
                personRules.bookingPrimeTimeExceeded ||
                personRules.daysAheadLimitExceeded ||
                personRules.maxMinutesPerDayExceeded ||
                personRules.bookingPrimeTimeExceeded ||
                personRules.maxBookingsPerDayExceeded
            );
        } else {
            return true;
        }
    }

    @Selector([CourtBookingSaveSelectors.firstPlayerCanBook, CourtBookingSaveSelectors.isCourtSelected])
    static typeSelectEnabled(playerCanBook: boolean, courtSelected: boolean) {
        return playerCanBook && courtSelected;
    }

    @Selector([CourtBookingSaveSelectors.firstPlayerCanBook, CourtBookingSaveSelectors.isCategoryOptionSelected])
    static timePeriodEnabled(playerCanBook: boolean, categoryOptionsSelected: boolean) {
        return playerCanBook && categoryOptionsSelected;
    }

    @Selector([CourtBookingSaveSelectors.firstPlayerCanBook, CourtBookingSaveSelectors.isTimePeriodSelected])
    static timeEnabled(playerCanBook: boolean, timePeriodSelected: boolean) {
        return playerCanBook && timePeriodSelected;
    }

    @Selector([CourtBookingSaveState])
    static secondPlayerRulesMatrix(state: CourtBookingSaveStateModel): CheckPersonRulesMatrixModel {
        if (state.playerRules.hasItems && state.courtBookingForm?.model?.secondPlayer) {
            return state.playerRules.items.find(pr => pr.personId === state.courtBookingForm.model.secondPlayer.personId);
        } else {
            return null;
        }
    }

    @Selector([CourtBookingSaveState])
    static thirdPlayerRulesMatrix(state: CourtBookingSaveStateModel): CheckPersonRulesMatrixModel {
        if (state.playerRules.hasItems && state.courtBookingForm?.model?.thirdPlayer) {
            return state.playerRules.items.find(pr => pr.personId === state.courtBookingForm.model.thirdPlayer.personId);
        } else {
            return null;
        }
    }

    @Selector([CourtBookingSaveState])
    static fourthPlayerRulesMatrix(state: CourtBookingSaveStateModel): CheckPersonRulesMatrixModel {
        if (state.playerRules.hasItems && state.courtBookingForm?.model?.fourthPlayer) {
            return state.playerRules.items.find(pr => pr.personId === state.courtBookingForm.model.fourthPlayer.personId);
        } else {
            return null;
        }
    }

    @Selector([CourtBookingSaveState,
        CourtBookingSaveSelectors.anyPlayerHasExceedRule,
        CourtBookingSaveSelectors.isSelectedTimeSlotUnavailable])
    static isFormValid(state: CourtBookingSaveStateModel,
        anyPlayersExceededRule: boolean,
        isSelectedTimeSlotUnavailable: boolean): boolean {
        return state.courtBookingForm?.status === 'VALID' && !anyPlayersExceededRule && !isSelectedTimeSlotUnavailable;
    }

    @Selector([CourtBookingSaveState])
    static anyPlayerHasExceedRule(state: CourtBookingSaveStateModel): boolean {
        return (
            state.playerRules.hasItems &&
            state.playerRules.items.some(
                pr =>
                    pr.daysAheadLimitExceeded ||
                    pr.maxBookingsPerDayExceeded ||
                    pr.maxMinutesPerDayExceeded ||
                    pr.courtBookingNotAllowed
            )
        );
    }

    @Selector([CourtBookingSaveSelectors.getSelectedTimeSlot])
    static isSelectedTimeSlotUnavailable(selectedTimeSlot: CourtBookingSaveTimeSlotModel): boolean {
        if (selectedTimeSlot) {
            return selectedTimeSlot.isUnavailable;
        }
    }

    @Selector([CourtBookingSaveSelectors.getSelectedTimeSlot])
    static selectedTimeSlotUnavailabilityMessage(selectedTimeSlot: CourtBookingSaveTimeSlotModel): string {
        if (selectedTimeSlot) {
            return selectedTimeSlot.unavailabilityMessage;
        }
    }

    @Selector([CourtBookingSaveState])
    static getSelectedTimeSlot(state: CourtBookingSaveStateModel): CourtBookingSaveTimeSlotModel {
        if (state.timeSlots.loaded && state.courtBookingForm?.model?.timeFromInMinutes) {
            const timeFromInMinutes = state.courtBookingForm.model.timeFromInMinutes;
            return state.timeSlots.items.find(x => x.timeFromInMinutes === timeFromInMinutes);
        }
    }

    @Selector([CourtBookingSaveState])
    static getPlayers(state: CourtBookingSaveStateModel): BcStateListContext<PersonModel> {
        return state.players;
    }

    @Selector([CourtBookingSaveState])
    static getCourts(state: CourtBookingSaveStateModel): BcStateListContext<CourtForAddBookingModel> {
        return state.courts;
    }

    @Selector([CourtBookingSaveState])
    static getCourtOptions(state: CourtBookingSaveStateModel): CourtForAddBookingOptionsModel[] {
        if (state.courts.loaded && state.courtBookingForm?.model?.courtId) {
            const court = state.courts.items.find(c => c.courtId === state.courtBookingForm.model.courtId);
            return court.courtOptions;
        } else {
            return [];
        }
    }

    @Selector([CourtBookingSaveSelectors.selectedCategoryOption])
    static getTimeSlots(selectedCategoryOption: CourtForAddBookingOptionsModel): CourtForAddBookingTimeSlotsModel[] {
        if (selectedCategoryOption) {
            const timeSlots = cloneDeep(selectedCategoryOption.timeSlots);
            return timeSlots.sort((ts1, ts2) => ts1.durationInMinutes - ts2.durationInMinutes);
        } else {
            return [];
        }
    }

    @Selector([CourtBookingSaveState, CourtBookingSaveSelectors.canSelectCategoryOption])
    static selectedCategoryOption(
        state: CourtBookingSaveStateModel,
        canSelectCategoryOption: boolean
    ): CourtForAddBookingOptionsModel {
        if (canSelectCategoryOption && state.courtBookingForm?.model?.courtId && state.courtBookingForm?.model?.categoryOptionsId) {
            const court = state.courts.items.find(c => c.courtId === state.courtBookingForm.model.courtId);
            const categoryOption = court.courtOptions.find(
                co => co.categoryOptionsId === state.courtBookingForm.model.categoryOptionsId
            );
            return categoryOption;
        } else {
            return null;
        }
    }

    @Selector([CourtBookingSaveSelectors.selectedCategoryOption])
    static selectedCategoryName(selectedCategoryOption: CourtForAddBookingOptionsModel): string {
        if (selectedCategoryOption) {
            return selectedCategoryOption.category.name;
        } else {
            return null;
        }
    }

    @Selector([CourtBookingSaveState])
    static getAvailableTimeSlots(state: CourtBookingSaveStateModel): BcStateListContext<CourtBookingSaveTimeSlotModel> {
        return state.timeSlots;
    }

    @Selector([CourtBookingSaveState])
    static isMainPlayerSelected(state: CourtBookingSaveStateModel): boolean {
        return !!state.courtBookingForm?.model?.firstPlayer;
    }

    @Selector([CourtBookingSaveState, CourtBookingSaveSelectors.isMainPlayerSelected])
    static isCourtSelected(state: CourtBookingSaveStateModel, mainPlayerSelected: boolean): boolean {
        return !!(
            mainPlayerSelected &&
            state.courtBookingForm?.model?.courtId
        );
    }

    @Selector([CourtBookingSaveState, CourtBookingSaveSelectors.isMainPlayerSelected])
    static isCategoryOptionSelected(state: CourtBookingSaveStateModel, mainPlayerSelected: boolean): boolean {
        return !!(
            mainPlayerSelected &&
            state.courtBookingForm?.model?.categoryOptionsId
        );
    }

    @Selector([CourtBookingSaveState])
    static canSelectCategoryOption(state: CourtBookingSaveStateModel): boolean {
        return !!(
            state.courts.loaded &&
            state.courtBookingForm?.model?.courtId &&
            state.courtBookingForm?.model?.categoryOptionsId
        );
    }

    @Selector([CourtBookingSaveState, CourtBookingSaveSelectors.isMainPlayerSelected])
    static isTimePeriodSelected(state: CourtBookingSaveStateModel, mainPlayerSelected: boolean): boolean {
        return !!(
            mainPlayerSelected &&
            state.courtBookingForm?.model?.timeSlot
        );
    }

    @Selector([CourtBookingSaveSelectors.selectedCategoryOption])
    static showTwoPlayers(selectedCategoryOption: CourtForAddBookingOptionsModel): boolean {
        if (selectedCategoryOption) {
            return selectedCategoryOption.playersCount === 2;
        } else {
            return false;
        }
    }

    @Selector([CourtBookingSaveSelectors.selectedCategoryOption])
    static showFourPlayers(selectedCategoryOption: CourtForAddBookingOptionsModel): boolean {
        if (selectedCategoryOption) {
            return selectedCategoryOption.playersCount === 4;
        } else {
            return false;
        }
    }

    @Selector([CourtBookingSaveState])
    static showFormSpinner(state: CourtBookingSaveStateModel): boolean {
        return state.players.isLoading || state.playerRules.isLoading || state.courts.isLoading || state.timeSlots.isLoading;
    }

    @Selector([CourtBookingSaveState])
    static formLoading(state: CourtBookingSaveStateModel): boolean {
        return !!state.courtBookingForm?.isLoading;
    }
}
